import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import {
  deleteMessage,
  getConversationDetailsForNextPage,
  getLatestConversationsDetailsUpdates,
  setConversationAsSeen,
  setMessageDeletionState,
  setMessagesBox
} from '../../../redux/slices/communication';

import { useTranslate } from '@tolgee/react';
import Message from './message';
import { FACEBOOK, INSTAGRAM, WHATSAPP } from '../../../config/constants';
import { Button, Modal } from 'react-bootstrap';
import { TextArea } from './textArea/textArea';
import MessagesBoxLoader from '../loaders/MessagesBoxLoader';

// space
import { postTypePermissionMap } from '../utils/permissionsUtils';
import { PLATFORM_STATIC_ICONS_PNG } from '../../../config/icons';
import MessagesPlaceholderImage from '../../../assets/images/placeholders/messages/messages.png';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useIdleTimer } from 'react-idle-timer';
import { PULLING_INTERVAL_MESSAGES, TIME_BEFORE_IDLE } from '../constants';
import { toggleTemplateModal } from '../../../redux/slices/whatsappTemplate';
import { OpenWhatsAppTemplateModal } from '../../whatsappTemplateBuilder/components/OpenWhatsAppTemplateModal';

export default function Messages() {
  const {
    data: {
      conversationDetails: { data, isLoading },
      activeConversation
    },
    components: { messageBoxWidth }
  } = useSelector((state) => state.communication);
  const divRef = useRef(null);
  const dispatch = useDispatch();
  useEffect(() => {
    if (divRef) {
      dispatch(setMessagesBox(divRef));
    }
  }, []);

  const [showWhatsappTemplateInfo, setShowWhatsappTemplateInfo] =
    useState(false);
  const [showUpgradeRequird, setShowUpgradeRequird] = useState(false);

  const { t } = useTranslate();
  const [isIdle, setIsIdle] = useState(false);
  const onIdle = () => {
    setIsIdle(true);
  };

  const onActive = () => {
    setIsIdle(false);
  };

  useIdleTimer({
    timeout: TIME_BEFORE_IDLE, // 5 minutes
    onIdle,
    onActive,
    debounce: 250
  });
  // register a pulling function to get the latest data for conversation details
  useEffect(() => {
    // when user goes idle, stop pulling the data
    const interval = setInterval(() => {
      // check if user is idle
      if (isIdle) return;
      if (!activeConversation) return;
      // get the latest data for the conversation
      dispatch(getLatestConversationsDetailsUpdates(activeConversation?.uuid));
    }, PULLING_INTERVAL_MESSAGES);
    if (isIdle) clearInterval(interval);
    return () => clearInterval(interval);
  }, [isIdle, activeConversation]);

  useEffect(() => {
    if (activeConversation) {
      if (activeConversation.read === false) {
        dispatch(setConversationAsSeen(activeConversation?.uuid));
      }
    }
  }, [activeConversation]);

  const getMinutesBetweenDates = function (startDate, endDate) {
    const diff = endDate.getTime() - startDate.getTime();

    return diff / 60000;
  };

  useEffect(() => {
    if (!activeConversation) {
      setShowWhatsappTemplateInfo(false);
      setShowUpgradeRequird(false);
      return;
    }

    const lastUpdated = new Date(activeConversation?.platform_updated);
    const diff = getMinutesBetweenDates(lastUpdated, new Date());
    const olderThan24Hours = diff > 60 * 24;
    setShowWhatsappTemplateInfo(
      activeConversation?.platform === WHATSAPP && olderThan24Hours
    );
  }, [activeConversation, showWhatsappTemplateInfo]);

  return (
    <div
      ref={divRef}
      className="w-100 d-flex flex-column container justify-content-between"
      style={{ minWidth: messageBoxWidth, minHeight: '100%' }}
    >
      <DeleteConfirmation />
      {isLoading && (
        <div className="card h-100 d-flex  align-items-center p-10">
          <MessagesBoxLoader />
        </div>
      )}
      {!isLoading && activeConversation && (
        <MessagesEntries data={data} activeConversation={activeConversation} />
      )}
      {!activeConversation && !isLoading && (
        <div className="card d-flex justify-content-center align-items-center h-100 user-select-none">
          <img
            src={MessagesPlaceholderImage}
            alt="messages-placeholder"
            width={200}
          />
          <span className="mt-10 text-muted fw-bolder">
            {t('communication.messages.no_conversation_selected')}
          </span>
        </div>
      )}
      {showWhatsappTemplateInfo && (
        <div
          className={
            'card m-1 h-25 d-flex align-items-center justify-content-center min-h-250px'
          }
        >
          <div className="w-75 text-center text-muted">
            {t('communication.messages.whatsapp_template_answer_required')}
          </div>
          <div className="d-flex mt-6">
            <OpenWhatsAppTemplateModal />
          </div>
        </div>
      )}
      {!showWhatsappTemplateInfo && (
        <>
          {activeConversation && !isLoading && activeConversation.type && (
            <>
              <div className="mt-5" />
              {postTypePermissionMap[activeConversation.type] ? (
                activeConversation.platform === INSTAGRAM &&
                data?.data?.length === 0 ? null : (
                  <TextArea />
                )
              ) : (
                <div
                  className={
                    'card m-1 h-25 d-flex align-items-center justify-content-center min-h-250px'
                  }
                >
                  <div
                    className="alert alert-danger w-75 text-center"
                    role="alert"
                  >
                    {t('communication.messages.messages_disabled')}
                  </div>
                  <div className="d-flex flex-end">
                    <a
                      href="/subscription-management/select/"
                      className={'fw-bolder text-primary'}
                    >
                      {t('communication.features.upgrade_subscription')}
                    </a>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export const MessagesEntries = ({ data, activeConversation }) => {
  const dispatch = useDispatch();
  const {
    communication: {
      data: { pandingMessages }
    }
  } = useSelector((state) => state);

  const { t } = useTranslate();

  // A ref that points to the bottom "anchor" of our messages
  const messagesEndRef = useRef(null);

  // Whenever the conversation data changes (including pending messages),
  // scroll to the bottom.
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [data, pandingMessages, activeConversation]);

  // No conversation selected
  if (!activeConversation) {
    return (
      <div className="card d-flex justify-content-center align-items-center h-100 user-select-none">
        {t('communication.messages.no_conversation_selected')}
      </div>
    );
  }

  // Instagram conversation with no messages
  if (
    (!data || data?.data?.length === 0) &&
    activeConversation.platform === INSTAGRAM
  ) {
    return (
      <div className="card p-5 d-flex overflow-scroll flex-grow-1 justify-content-center align-items-center">
        <img
          src={PLATFORM_STATIC_ICONS_PNG[activeConversation?.platform]}
          alt="platform-icon"
          className="h-50px w-50px"
        />
        <p className="text-muted mt-2">
          {t(
            'lomavis_communication.messages.instagram_no_messages_in_conversation'
          )}
        </p>
      </div>
    );
  }

  return (
    <div
      className="card p-5 flex-grow-1"
      id="scrollableDiv"
      style={{
        overflow: 'auto', // Enable scrolling
        display: 'flex',
        flexDirection: 'column-reverse', // Newest messages visually at the bottom
        position: 'relative'
      }}
    >
      {/*
        "mt-auto" ensures messages stack at the bottom,
        since we're using column-reverse.
      */}
      <div className="mt-auto" />
      <InfiniteScroll
        dataLength={
          (data?.data?.length || 0) +
          (pandingMessages?.[activeConversation.uuid]?.length || 0)
        }
        next={() => {
          dispatch(getConversationDetailsForNextPage(() => {}));
        }}
        hasMore={data?.nextPage ?? false}
        inverse={true} // We load older messages by scrolling up
        loader={<></>} // Loader can be empty or a spinner
        scrollableTarget="scrollableDiv"
      >
        {/* Render the existing messages */}
        {data?.data?.map((message) => {
          if (message.deleted_on_platform) return null;
          return (
            <div
              key={message.uuid}
              className="mt-5"
              style={{ marginLeft: message.is_child ? 45 : undefined }}
            >
              <Message
                data={message}
                left={
                  !message.is_sent_by_me ||
                  activeConversation?.type === 'comment' ||
                  (activeConversation?.type === 'review' &&
                    activeConversation.platform === FACEBOOK)
                }
                rules={activeConversation?.rules}
              />
            </div>
          );
        })}

        {/* Render any "pending" messages that haven't completed sending */}
        {pandingMessages?.[activeConversation.uuid]?.map((message, i) => (
          <div
            key={i}
            className="mt-5"
            style={{
              marginLeft:
                activeConversation.type === 'comment' ||
                activeConversation.type === 'review'
                  ? 45
                  : undefined
            }}
          >
            <Message
              data={message}
              left={
                !message.is_sent_by_me ||
                activeConversation?.type === 'comment' ||
                (activeConversation?.type === 'review' &&
                  activeConversation.platform === FACEBOOK)
              }
              rules={{}}
            />
          </div>
        ))}

        {/*
          The "anchor" div at the end for scrolling to the bottom.
          On new messages, we call scrollIntoView on this div.
        */}
        <div ref={messagesEndRef} />
      </InfiniteScroll>
      {/* Example loader at the bottom if there's more data to load */}
      {data?.data?.length < data?.length && (
        <div className="d-flex align-items-center justify-content-center">
          <div className="spinner-border text-secondary" role="status">
            <span className="sr-only">
              {t('lomavis_communication.loading')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export const DeleteConfirmation = () => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const setShow = (value) => {
    dispatch(setMessageDeletionState(value));
  };
  const handleDelete = () => {
    dispatch(
      deleteMessage(
        data?.uuid,
        t('communication.messages.message_deleted_successfully')
      )
    );
    dispatch(setMessageDeletionState(false));
  };
  const {
    data: {
      selectedMessage: { deleteMessageState, data }
    }
  } = useSelector((state) => state.communication);
  return (
    <Modal show={deleteMessageState} onHide={() => {}} size="md" centered>
      <Modal.Header className="border-0">
        <Modal.Title>
          {t('communication.messages.confirm_deletion_title')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="border-0">
        {t('communication.messages.confirm_deletion')}
      </Modal.Body>
      <Modal.Footer className="border-0">
        <Button variant="secondary" size="sm" onClick={() => setShow(false)}>
          {t('communication.messages.close')}
        </Button>
        <Button variant="danger" size="sm" onClick={handleDelete}>
          {t('communication.messages.delete')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
